import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { isAuthInvalidError } from '../gateway';
import { AuthService } from './auth.service';
import { TokenService } from './token.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  async canActivate(url: string): Promise<boolean> {
    if (!this.tokenService.token && !this.tokenService.refreshToken) {
      void this.router.navigate(['/signin'], { queryParams: { returnUrl: url ?? '/' } });
      return false;
    }
    try {
      if (this.tokenService.token && !this.tokenService.isExpired()) {
        await this.authService.validateSession();
        return true;
      }
      await this.authService.refresh();
      return true;
    } catch (error: unknown) {
      // Only a real 401/403 ends the session. A network drop or the auth server
      // restarting must not throw an operator back to the login screen mid-sale —
      // the tokens are still there and the refresh scheduler keeps retrying.
      if (!isAuthInvalidError(error)) {
        return true;
      }
      void this.router.navigate(['/signin'], { queryParams: { returnUrl: url ?? '/' } });
      return false;
    }
  }
}

export const canActivateRoute: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return inject(AuthGuard).canActivate(state.url);
};
